import { MultilingualWidgetConfig } from '../dashboard.widgets.config';

interface HospitalLocationConfig {
  mapUrl: {
    en: string;
    de: string;
  };
  uiText: {
    header: {
      en: string;
      de: string;
    };
    subtitle: {
      en: string;
      de: string;
    };
    buttons: {
      directions: {
        en: string;
        de: string;
      };
      openFullMap: {
        en: string;
        de: string;
      };
    };
    loading: {
      en: string;
      de: string;
    };
  };
}

const hospitalLocationConfig: HospitalLocationConfig = {
  mapUrl: {
    en: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2427.7533782429313!2d13.375324376944633!3d52.52530197198459!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a851e688a8a6b5%3A0x264a04442b228d43!2sCharit%C3%A9%20-%20Universit%C3%A4tsmedizin%20Berlin!5e0!3m2!1sen!2sus!4v1711210458079!5m2!1sen!2sus',
    de: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2427.7533782429313!2d13.375324376944633!3d52.52530197198459!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a851e688a8a6b5%3A0x264a04442b228d43!2sCharit%C3%A9%20-%20Universit%C3%A4tsmedizin%20Berlin!5e0!3m2!1sde!2sde!4v1711210458079!5m2!1sde!2sde',
  },
  uiText: {
    header: {
      en: 'Hospital Location',
      de: 'Krankenhausstandort',
    },
    subtitle: {
      en: 'Charité University Hospital',
      de: 'Charité Universitätsklinikum',
    },
    buttons: {
      directions: {
        en: 'Get Directions',
        de: 'Route planen',
      },
      openFullMap: {
        en: 'Full Map',
        de: 'Vollständige Karte',
      },
    },
    loading: {
      en: 'Loading map...',
      de: 'Karte wird geladen...',
    },
  },
};

const generateHospitalLocationContent = (language: 'en' | 'de'): string => {
  const mapUrl = hospitalLocationConfig.mapUrl[language];

  return `
  <div style="display: flex; flex-direction: column; height: 100%; font-family: 'Be Vietnam Pro', sans-serif; color: #282a32;">
    <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
      <div style="font-weight: 600; color: #282a32;">${hospitalLocationConfig.uiText.header[language]}</div>
      <div style="font-size: 0.85rem; color: #686b87;">${hospitalLocationConfig.uiText.subtitle[language]}</div>
    </div>
    <div style="flex: 1; display: flex; flex-direction: column; overflow: hidden;">
      <div style="padding: 6px 10px; background-color: #f3f4f6; border-radius: 8px 8px 0 0; display: flex; align-items: center; border: 1px solid #eff1f6; border-bottom: none;">
        <div style="display: flex; align-items: center; margin-right: 8px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#434ce8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <div style="flex: 1; font-size: 0.85rem; color: #434ce8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${language === 'en' ? 'Charitéplatz 1, 10117 Berlin, Germany' : 'Charitéplatz 1, 10117 Berlin, Deutschland'}
        </div>
      </div>
      <div style="flex: 1; border: 1px solid #eff1f6; border-radius: 0 0 8px 8px; overflow: hidden; position: relative; background-color: #f5f7fa;">
        <iframe src="${mapUrl}" style="width: 100%; height: 100%; border: none;" title="Charité Hospital Map" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
        <!-- Loading overlay with a 3 second auto-hide -->
        <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; justify-content: center; align-items: center; background-color: rgba(245, 247, 250, 0.7); font-size: 0.9rem; color: #686b87; animation: fadeOut 3s forwards 1s;">
          <div style="display: flex; flex-direction: column; align-items: center;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#434ce8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 2s linear infinite; margin-bottom: 8px;">
              <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
              <path d="M12 2a10 10 0 0 1 10 10"/>
            </svg>
            ${hospitalLocationConfig.uiText.loading[language]}
          </div>
        </div>
      </div>
    </div>
    <div style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center;">
      <button style="display: flex; align-items: center; background-color: transparent; color: #434ce8; border: 1px solid #434ce8; border-radius: 8px; padding: 8px 12px; font-size: 0.85rem; font-weight: 500; cursor: pointer;">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
        ${hospitalLocationConfig.uiText.buttons.directions[language]}
      </button>
      <button style="display: flex; align-items: center; background-color: #434ce8; color: white; border: none; border-radius: 8px; padding: 8px 12px; font-size: 0.85rem; font-weight: 500; cursor: pointer;">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;">
          <path d="M15 3h6v6"></path>
          <path d="M10 14 21 3"></path>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
        </svg>
        ${hospitalLocationConfig.uiText.buttons.openFullMap[language]}
      </button>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes fadeOut {
        0% { opacity: 1; }
        100% { opacity: 0; visibility: hidden; }
      }
    </style>
  </div>`;
};

export const hospitalLocationWidget: MultilingualWidgetConfig = {
  id: 'hospital-location-map',
  translations: {
    en: {
      title: hospitalLocationConfig.uiText.header.en,
      content: generateHospitalLocationContent('en'),
    },
    de: {
      title: hospitalLocationConfig.uiText.header.de,
      content: generateHospitalLocationContent('de'),
    },
  },
  x: 0,
  y: 0,
  w: 3,
  h: 6,
};
